import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  GetOperatorProfileResponse,
  UpdateOperatorProfileRequest,
  UpdateOperatorProfileResponse,
} from '@dreamai/types';
import { formatLanguageTag } from './humanTaskLanguage';

interface Props {
  /** Called with the saved list whenever it changes, so the task list can refresh. */
  onSaved?: (languages: string[]) => void;
}

const splitTags = (raw: string): string[] =>
  raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);

/**
 * Self-service editor for the languages the signed-in operator can take phone
 * calls in. The backend canonicalises tags (nl_nl → nl-NL) and rejects ones it
 * cannot recognise; an empty list means English only.
 */
const OperatorLanguages: React.FC<Props> = ({ onSaved }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [languages, setLanguages] = useState<string[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetOperatorProfileResponse>(
          '/operator-profile',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.success && response.data.profile) {
          setLanguages(response.data.profile.languages);
        }
      } catch (err) {
        console.error('Error loading operator profile:', err);
      }
    };
    load();
  }, [getAccessTokenSilently]);

  const startEditing = () => {
    setDraft((languages ?? []).join(', '));
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const body: UpdateOperatorProfileRequest = {
        languages: splitTags(draft),
      };
      const response = await api.put<UpdateOperatorProfileResponse>(
        '/operator-profile',
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.profile) {
        setLanguages(response.data.profile.languages);
        setEditing(false);
        onSaved?.(response.data.profile.languages);
      } else {
        setError(response.data.error || 'Failed to save languages');
      }
    } catch (err: unknown) {
      const data = (
        err as { response?: { data?: UpdateOperatorProfileResponse } }
      ).response?.data;
      setError(data?.error || 'Failed to save languages');
    } finally {
      setSaving(false);
    }
  };

  if (languages === null) return null;

  const shown = languages.length > 0 ? languages : ['en'];

  return (
    <div className='operator-languages'>
      <span className='operator-languages-label'>I speak</span>
      {editing ? (
        <form
          className='operator-languages-form'
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <input
            type='text'
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder='en-GB, nl-NL, es-419'
            autoFocus
            disabled={saving}
          />
          <button type='submit' disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type='button'
            className='operator-languages-cancel'
            onClick={() => setEditing(false)}
            disabled={saving}
          >
            Cancel
          </button>
          {error && <span className='operator-languages-error'>{error}</span>}
        </form>
      ) : (
        <>
          {shown.map((tag) => (
            <span key={tag} className='operator-language-chip' title={tag}>
              {formatLanguageTag(tag)}
            </span>
          ))}
          <button
            type='button'
            className='operator-languages-edit'
            onClick={startEditing}
            title='Languages you can take phone calls in — used to route tasks to you'
          >
            Edit
          </button>
        </>
      )}
    </div>
  );
};

export default OperatorLanguages;
